// frontend/src/features/campaign/components/create/Step1BriefGoals.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from '../../../../api/axios';
import CampaignDateRangePicker from '../common/CampaignDateRangePicker';
import { addDays } from '../../utils/dateUtils';
import {
  CAMPAIGN_TYPES,
  INDUSTRY_SECTORS,
  TARGET_REGIONS,
  AVAILABLE_PERSONAS,
  normalizePersonaName,
  ALL_CHANNELS,
  PRESET_PRODUCTS,
  CURRENCY_OPTIONS,
  CURRENCIES,
  CURRENCY_NAMES,
  CURRENCY_SYMBOLS,
  CURRENCY_FLAGS,
  CURRENCY_COUNTRY_CODES,
} from '../../constants/campaignConstants';
import { INSPIRATION_PROMPTS, getBlueprintTemplateByPrompt } from '../../data/blueprintTemplates';

const FALLBACK_FX_RATES = {
  USD: 1.0,
  MYR: 4.45,
  SGD: 1.34,
  EUR: 0.86,
  GBP: 0.78,
  CNY: 7.23,
  JPY: 155.2,
  AUD: 1.52,
  HKD: 7.84,
  INR: 83.5,
  CAD: 1.37,
  CHF: 0.89,
  NZD: 1.64,
};

const escapeRegex = (s) => (s ? s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&') : '');

// Helper to separate currency code/symbol and amount
const parseBudgetCurrencyAndAmount = (rawBudget) => {
  if (!rawBudget || typeof rawBudget !== 'string') {
    return { currency: 'USD', amount: '' };
  }
  const str = rawBudget.trim();
  const sortedOptions = [...CURRENCY_OPTIONS].sort((a, b) => b.symbol.length - a.symbol.length);
  for (const opt of sortedOptions) {
    const codeEsc = escapeRegex(opt.code);
    const symEsc = escapeRegex(opt.symbol);
    const prefixRegex = new RegExp(`^(${codeEsc}|${symEsc})\\s*`, 'i');
    if (prefixRegex.test(str)) {
      const remaining = str.replace(prefixRegex, '').trim();
      return { currency: opt.code, amount: remaining };
    }
  }
  const fallbackClean = str.replace(/^[^\d]+/, '').trim();
  return { currency: 'USD', amount: fallbackClean || str };
};

// Format effective date as "9 Sept 2026"
const formatEffectiveDate = (dateStr) => {
  if (!dateStr) return '9 Sept 2026';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '9 Sept 2026';
    const day = d.getDate();
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sept',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch (e) {
    return '9 Sept 2026';
  }
};

export default function Step1BriefGoals({
  formData,
  setFormData,
  validationError,
  setValidationError,
  isReadOnly = false,
  onApplyPlaybook,
  onTogglePersona,
  onToggleChannel,
  isGeneratingBlueprint: isGeneratingBlueprintProp,
  setIsGeneratingBlueprint: setIsGeneratingBlueprintProp,
}) {
  const [aiPrompt, setAiPrompt] = useState('');
  const [internalGenerating, setInternalGenerating] = useState(false);
  const isGeneratingAi =
    isGeneratingBlueprintProp !== undefined ? isGeneratingBlueprintProp : internalGenerating;
  const setIsGeneratingAi = (val) => {
    setInternalGenerating(val);
    if (setIsGeneratingBlueprintProp) setIsGeneratingBlueprintProp(val);
  };
  const [aiError, setAiError] = useState('');

  // ── Live Exchange Rates from Currency Rate API (Frankfurter / ECB) ──
  const [fxRates, setFxRates] = useState(null);
  const [fxRateMeta, setFxRateMeta] = useState(null);

  // ── Rich Popover State ──
  const currencyPopoverRef = useRef(null);
  const [showCurrencyPopover, setShowCurrencyPopover] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (currencyPopoverRef.current && !currencyPopoverRef.current.contains(e.target)) {
        setShowCurrencyPopover(false);
      }
    };
    if (showCurrencyPopover) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showCurrencyPopover]);

  useEffect(() => {
    let isMounted = true;
    const fetchLiveRates = async () => {
      try {
        const res = await axios.get('/currency/rates');
        if (res.data?.success && res.data?.data?.rates && isMounted) {
          setFxRates(res.data.data.rates);
          setFxRateMeta(res.data.data);
        }
      } catch (err) {
        console.warn(
          '[Step1BriefGoals] Could not fetch live FX rates, using fallback baselines:',
          err.message
        );
      }
    };
    fetchLiveRates();
    return () => {
      isMounted = false;
    };
  }, []);

  // ── Auto-heal / Ensure End Date is always populated ──
  useEffect(() => {
    if (formData.startDate && (!formData.endDate || formData.endDate === '')) {
      const dur = Number(formData.durationDays) || 45;
      const end = addDays(formData.startDate, dur);
      setFormData((prev) => ({
        ...prev,
        endDate: prev.endDate || end,
        durationDays: prev.durationDays || dur,
      }));
    }
  }, [formData.startDate, formData.endDate, formData.durationDays, setFormData]);

  // ── Currency & Budget Input State ──
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    const parsed = parseBudgetCurrencyAndAmount(formData.allocatedBudget);
    return formData.currency || parsed.currency || 'USD';
  });
  const [budgetAmountInput, setBudgetAmountInput] = useState(() => {
    const parsed = parseBudgetCurrencyAndAmount(formData.allocatedBudget);
    return parsed.amount || '';
  });

  useEffect(() => {
    const { currency: parsedCurr, amount: parsedAmt } = parseBudgetCurrencyAndAmount(
      formData.allocatedBudget
    );
    if (formData.currency && formData.currency !== currentCurrency) {
      setCurrentCurrency(formData.currency);
    } else if (parsedCurr && parsedCurr !== currentCurrency && formData.allocatedBudget) {
      setCurrentCurrency(parsedCurr);
    }
    setBudgetAmountInput(parsedAmt);
  }, [formData.allocatedBudget, formData.currency]);

  const handleCurrencyChange = (newCode) => {
    if (isReadOnly || isGeneratingAi) return;
    const prevCode = currentCurrency;
    setCurrentCurrency(newCode);

    const targetOption = CURRENCY_OPTIONS.find((c) => c.code === newCode) || CURRENCY_OPTIONS[0];

    // Auto-convert numeric value using live FX rates (or fallback) if amount exists
    const numericStr = (budgetAmountInput || '').replace(/[^0-9.]/g, '');
    let newAmount = budgetAmountInput ? budgetAmountInput.replace(/^[^\d]+/, '').trim() : '';
    const prevRate = (fxRates && fxRates[prevCode]) || FALLBACK_FX_RATES[prevCode] || 1;
    const newRate = (fxRates && fxRates[newCode]) || FALLBACK_FX_RATES[newCode] || 1;

    if (numericStr && !isNaN(Number(numericStr)) && prevRate && newRate) {
      const val = parseFloat(numericStr);
      const valInUsd = val / prevRate;
      const convertedVal = valInUsd * newRate;
      const rounded = Math.round(convertedVal);
      newAmount = rounded.toLocaleString();
      setBudgetAmountInput(newAmount);
    }

    const updatedBudgetString = newAmount ? `${targetOption.symbol} ${newAmount}` : '';
    setFormData((prev) => ({
      ...prev,
      currency: newCode,
      allocatedBudget: updatedBudgetString,
    }));
    if (validationError) setValidationError('');
  };

  const handleBudgetAmountChange = (e) => {
    if (isReadOnly || isGeneratingAi) return;
    const rawVal = e.target.value;
    let cleanVal = rawVal;
    const sortedOptions = [...CURRENCY_OPTIONS].sort((a, b) => b.symbol.length - a.symbol.length);
    for (const opt of sortedOptions) {
      const codeEsc = escapeRegex(opt.code);
      const symEsc = escapeRegex(opt.symbol);
      const prefixRegex = new RegExp(`^(${codeEsc}|${symEsc})\\s*`, 'i');
      if (prefixRegex.test(cleanVal)) {
        cleanVal = cleanVal.replace(prefixRegex, '').trim();
      }
    }
    setBudgetAmountInput(cleanVal);

    const targetOption =
      CURRENCY_OPTIONS.find((c) => c.code === currentCurrency) || CURRENCY_OPTIONS[0];
    const updatedBudgetString = cleanVal.trim() ? `${targetOption.symbol} ${cleanVal.trim()}` : '';

    setFormData((prev) => ({
      ...prev,
      currency: currentCurrency,
      allocatedBudget: updatedBudgetString,
    }));
    if (validationError) setValidationError('');
  };

  // ── Session-based Custom Options (User-entered) ──
  const [userAddedCampaignTypes, setUserAddedCampaignTypes] = useState([]);
  const [isAddingCampaignType, setIsAddingCampaignType] = useState(false);
  const [newCampaignTypeInput, setNewCampaignTypeInput] = useState('');

  const [userAddedIndustrySectors, setUserAddedIndustrySectors] = useState([]);
  const [isAddingIndustry, setIsAddingIndustry] = useState(false);
  const [newIndustryInput, setNewIndustryInput] = useState('');

  const [userAddedTargetRegions, setUserAddedTargetRegions] = useState([]);
  const [isAddingRegion, setIsAddingRegion] = useState(false);
  const [newRegionInput, setNewRegionInput] = useState('');

  const [userAddedPersonas, setUserAddedPersonas] = useState([]);
  const [isAddingPersona, setIsAddingPersona] = useState(false);
  const [newPersonaInput, setNewPersonaInput] = useState('');

  // Dynamically compute options without accumulating obsolete AI generations
  const customCampaignTypes = useMemo(() => {
    const list = [...CAMPAIGN_TYPES, ...userAddedCampaignTypes];
    if (formData.campaignType && !list.includes(formData.campaignType)) {
      list.push(formData.campaignType);
    }
    return list;
  }, [userAddedCampaignTypes, formData.campaignType]);

  const customIndustrySectors = useMemo(() => {
    const list = [...INDUSTRY_SECTORS, ...userAddedIndustrySectors];
    if (formData.industrySector && !list.includes(formData.industrySector)) {
      list.push(formData.industrySector);
    }
    return list;
  }, [userAddedIndustrySectors, formData.industrySector]);

  const customTargetRegions = useMemo(() => {
    const list = [...TARGET_REGIONS, ...userAddedTargetRegions];
    if (formData.targetRegion && !list.includes(formData.targetRegion)) {
      list.push(formData.targetRegion);
    }
    return list;
  }, [userAddedTargetRegions, formData.targetRegion]);

  const customPersonas = useMemo(() => {
    const list = [...AVAILABLE_PERSONAS, ...userAddedPersonas];
    (formData.targetPersonas || []).forEach((rawP) => {
      const p = normalizePersonaName(rawP);
      if (!list.includes(p)) list.push(p);
    });
    return list;
  }, [userAddedPersonas, formData.targetPersonas]);

  const handleSelectTemplate = (templateName) => {
    if (isReadOnly || isGeneratingAi) return;
    setAiPrompt(templateName);
    const template = getBlueprintTemplateByPrompt(templateName);
    if (template) {
      setAiError('');
      setUserAddedPersonas([]);
      setUserAddedCampaignTypes([]);
      setUserAddedIndustrySectors([]);
      setUserAddedTargetRegions([]);
      onApplyPlaybook(template);
    }
  };

  const handleGenerateRealtimeBlueprint = async () => {
    const textToUse = (aiPrompt || '').trim();
    if (!textToUse) return;

    setIsGeneratingAi(true);
    setAiError('');
    try {
      const res = await axios.post('/outreach/generate-blueprint', {
        prompt: textToUse,
      });

      if (res.data?.success && res.data?.data) {
        setUserAddedPersonas([]);
        setUserAddedCampaignTypes([]);
        setUserAddedIndustrySectors([]);
        setUserAddedTargetRegions([]);
        onApplyPlaybook(res.data.data);
      } else {
        setAiError('Failed to generate real-time blueprint. Please try again.');
      }
    } catch (err) {
      console.error('[Step1BriefGoals] AI generation error:', err);
      setAiError(err.response?.data?.error || err.message || 'AI generation failed');
    } finally {
      setIsGeneratingAi(false);
    }
  };

  const handleConfirmAddCampaignType = () => {
    const trimmed = newCampaignTypeInput.trim();
    if (trimmed) {
      if (!userAddedCampaignTypes.includes(trimmed) && !CAMPAIGN_TYPES.includes(trimmed)) {
        setUserAddedCampaignTypes((prev) => [...prev, trimmed]);
      }
      setFormData((prev) => ({ ...prev, campaignType: trimmed }));
      if (validationError) setValidationError('');
    }
    setIsAddingCampaignType(false);
    setNewCampaignTypeInput('');
  };

  const handleConfirmAddIndustry = () => {
    const trimmed = newIndustryInput.trim();
    if (trimmed) {
      if (!userAddedIndustrySectors.includes(trimmed) && !INDUSTRY_SECTORS.includes(trimmed)) {
        setUserAddedIndustrySectors((prev) => [...prev, trimmed]);
      }
      setFormData((prev) => ({ ...prev, industrySector: trimmed }));
      if (validationError) setValidationError('');
    }
    setIsAddingIndustry(false);
    setNewIndustryInput('');
  };

  const handleConfirmAddRegion = () => {
    const trimmed = newRegionInput.trim();
    if (trimmed) {
      if (!userAddedTargetRegions.includes(trimmed) && !TARGET_REGIONS.includes(trimmed)) {
        setUserAddedTargetRegions((prev) => [...prev, trimmed]);
      }
      setFormData((prev) => ({ ...prev, targetRegion: trimmed }));
      if (validationError) setValidationError('');
    }
    setIsAddingRegion(false);
    setNewRegionInput('');
  };

  const handleConfirmAddPersona = () => {
    const trimmed = newPersonaInput.trim();
    if (trimmed) {
      const normalized = normalizePersonaName(trimmed);
      if (!userAddedPersonas.includes(normalized) && !AVAILABLE_PERSONAS.includes(normalized)) {
        setUserAddedPersonas((prev) => [...prev, normalized]);
      }
      if (!formData.targetPersonas.includes(normalized)) {
        onTogglePersona(normalized);
      }
    }
    setIsAddingPersona(false);
    setNewPersonaInput('');
  };

  return (
    <div className={`cmp-blueprint-step-pane ${isReadOnly ? 'read-only' : ''}`}>
      {/* Step Sub-Header Pill */}
      <div className="cmp-blueprint-tag-pill">STEP 1 OF 6 • CAMPAIGN BRIEF & BLUEPRINT</div>

      {/* Step Heading */}
      <h2 className="cmp-blueprint-heading">Campaign Brief & Business Objectives</h2>
      <p className="cmp-blueprint-subheading">
        Define the fundamental scope, target market, budget parameters, and business goals for this
        campaign.
      </p>

      {/* Real-Time AI Generator Bar */}
      {!isReadOnly && (
        <div
          style={{
            background: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: '12px',
            padding: '16px 18px',
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px',
              flexWrap: 'wrap',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontSize: '13.5px',
                fontWeight: 700,
                color: '#1e293b',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <i className="fas fa-magic" style={{ color: '#6366f1' }} /> Real-Time AI Campaign
              Strategy Generator
            </span>
            <span className="cmp-ai-powered-badge">
              <i className="fas fa-brain" /> Powered by AI Engine
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b' }}>
            Describe your product, target vertical, or strategy to auto-generate all 6 campaign
            creation steps in real time:
          </p>

          <div className="cmp-blueprint-ai-gen-bar">
            <div className="cmp-blueprint-input-wrap">
              <i className="fas fa-sparkles cmp-blueprint-input-icon" />
              <input
                type="text"
                className="cmp-blueprint-gen-input"
                placeholder="e.g. Enterprise CyberSecurity for Banking CISOs in Malaysia & Singapore..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGenerateRealtimeBlueprint();
                }}
                disabled={isGeneratingAi}
              />
            </div>
            <button
              type="button"
              className="cmp-btn-primary cmp-blueprint-gen-submit-btn"
              onClick={() => handleGenerateRealtimeBlueprint()}
              disabled={isGeneratingAi || !aiPrompt.trim()}
            >
              {isGeneratingAi ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Generating...
                </>
              ) : (
                <>
                  <i className="fas fa-bolt" /> Auto-Generate
                </>
              )}
            </button>
          </div>

          <div className="cmp-blueprint-inspo-row">
            <span className="cmp-inspo-label">Campaign Templates:</span>
            <div className="cmp-inspo-chips">
              {INSPIRATION_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="cmp-inspo-chip"
                  disabled={isGeneratingAi}
                  onClick={() => handleSelectTemplate(prompt)}
                >
                  <i className="fas fa-layer-group" /> {prompt}
                </button>
              ))}
            </div>
          </div>

          {aiError && (
            <div className="cmp-blueprint-error-box">
              <i className="fas fa-exclamation-triangle" /> {aiError}
            </div>
          )}
        </div>
      )}

      {/* Form Grid */}
      <div className="cmp-blueprint-form-grid">
        {/* Campaign Title */}
        <div className="cmp-field-group full-width">
          <label className="cmp-field-label">
            <span>
              Campaign Title <span className="text-danger">*</span>
            </span>
          </label>
          <input
            type="text"
            className="cmp-field-input"
            value={formData.title}
            disabled={isReadOnly || isGeneratingAi}
            onChange={(e) => {
              if (isReadOnly || isGeneratingAi) return;
              setFormData({ ...formData, title: e.target.value });
              if (validationError) setValidationError('');
            }}
            placeholder="e.g. SEA Enterprise Fintech & SaaS Q4 Outbound"
          />
        </div>

        {/* Primary Objective */}
        <div className="cmp-field-group full-width">
          <label className="cmp-field-label">
            <span>
              Primary Objective <span className="text-danger">*</span>
            </span>
          </label>
          <input
            type="text"
            className="cmp-field-input"
            value={formData.primaryObjective}
            disabled={isReadOnly || isGeneratingAi}
            onChange={(e) => {
              if (isReadOnly || isGeneratingAi) return;
              setFormData({ ...formData, primaryObjective: e.target.value });
              if (validationError) setValidationError('');
            }}
            placeholder="e.g. Accelerate Enterprise Demo Bookings & Net-New ARR Growth"
          />
        </div>

        {/* Product / Solution */}
        <div className="cmp-field-group full-width">
          <label className="cmp-field-label">
            <span>
              Product / Service <span className="text-danger">*</span>
            </span>
          </label>
          <input
            type="text"
            className="cmp-field-input"
            value={formData.product}
            disabled={isReadOnly || isGeneratingAi}
            onChange={(e) => {
              if (isReadOnly || isGeneratingAi) return;
              setFormData({ ...formData, product: e.target.value });
            }}
            placeholder="e.g. Enterprise AI Lead Gen & Pipeline Engine"
            list="cmp-product-presets"
          />
          <datalist id="cmp-product-presets">
            {PRESET_PRODUCTS.map((prod) => (
              <option key={prod} value={prod} />
            ))}
          </datalist>
        </div>

        {/* Campaign Type */}
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span>
              Campaign Type <span className="text-danger">*</span>
            </span>
            {!isReadOnly && !isGeneratingAi && !isAddingCampaignType && (
              <button
                type="button"
                className="cmp-btn-inline-add-custom"
                onClick={() => setIsAddingCampaignType(true)}
                title="Add a custom campaign type for this session"
              >
                <i className="fas fa-plus" /> Custom
              </button>
            )}
          </label>
          {isAddingCampaignType ? (
            <div className="cmp-custom-add-row">
              <input
                type="text"
                className="cmp-field-input-sm"
                placeholder="Enter custom campaign type..."
                value={newCampaignTypeInput}
                disabled={isGeneratingAi}
                autoFocus
                onChange={(e) => setNewCampaignTypeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmAddCampaignType();
                  } else if (e.key === 'Escape') {
                    setIsAddingCampaignType(false);
                    setNewCampaignTypeInput('');
                  }
                }}
              />
              <button
                type="button"
                className="cmp-btn-sm-add"
                disabled={isGeneratingAi}
                onClick={handleConfirmAddCampaignType}
              >
                Add
              </button>
              <button
                type="button"
                className="cmp-btn-sm-cancel"
                disabled={isGeneratingAi}
                onClick={() => {
                  setIsAddingCampaignType(false);
                  setNewCampaignTypeInput('');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <select
              className="cmp-field-select"
              value={formData.campaignType}
              disabled={isReadOnly || isGeneratingAi}
              onChange={(e) => {
                if (isReadOnly || isGeneratingAi) return;
                if (e.target.value === '__ADD_NEW__') {
                  setIsAddingCampaignType(true);
                  return;
                }
                setFormData({ ...formData, campaignType: e.target.value });
                if (validationError) setValidationError('');
              }}
            >
              <option value="">Select Campaign Type...</option>
              {customCampaignTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
              <option value="__ADD_NEW__">+ Add Custom Campaign Type...</option>
            </select>
          )}
        </div>

        {/* Industry Sector */}
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span>
              Target Company Type <span className="text-danger">*</span>
            </span>
            {!isReadOnly && !isGeneratingAi && !isAddingIndustry && (
              <button
                type="button"
                className="cmp-btn-inline-add-custom"
                onClick={() => setIsAddingIndustry(true)}
                title="Add a custom company type for this session"
              >
                <i className="fas fa-plus" /> Custom
              </button>
            )}
          </label>
          {isAddingIndustry ? (
            <div className="cmp-custom-add-row">
              <input
                type="text"
                className="cmp-field-input-sm"
                placeholder="Enter custom company type..."
                value={newIndustryInput}
                disabled={isGeneratingAi}
                autoFocus
                onChange={(e) => setNewIndustryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmAddIndustry();
                  } else if (e.key === 'Escape') {
                    setIsAddingIndustry(false);
                    setNewIndustryInput('');
                  }
                }}
              />
              <button
                type="button"
                className="cmp-btn-sm-add"
                disabled={isGeneratingAi}
                onClick={handleConfirmAddIndustry}
              >
                Add
              </button>
              <button
                type="button"
                className="cmp-btn-sm-cancel"
                disabled={isGeneratingAi}
                onClick={() => {
                  setIsAddingIndustry(false);
                  setNewIndustryInput('');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <select
              className="cmp-field-select"
              value={formData.industrySector}
              disabled={isReadOnly || isGeneratingAi}
              onChange={(e) => {
                if (isReadOnly || isGeneratingAi) return;
                if (e.target.value === '__ADD_NEW__') {
                  setIsAddingIndustry(true);
                  return;
                }
                setFormData({ ...formData, industrySector: e.target.value });
                if (validationError) setValidationError('');
              }}
            >
              <option value="">Select Industry Sector...</option>
              {customIndustrySectors.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              <option value="__ADD_NEW__">+ Add Custom Company Type...</option>
            </select>
          )}
        </div>

        {/* Target Region */}
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span>
              Target Region <span className="text-danger">*</span>
            </span>
            {!isReadOnly && !isGeneratingAi && !isAddingRegion && (
              <button
                type="button"
                className="cmp-btn-inline-add-custom"
                onClick={() => setIsAddingRegion(true)}
                title="Add a custom target region for this session"
              >
                <i className="fas fa-plus" /> Custom
              </button>
            )}
          </label>
          {isAddingRegion ? (
            <div className="cmp-custom-add-row">
              <input
                type="text"
                className="cmp-field-input-sm"
                placeholder="Enter custom target region..."
                value={newRegionInput}
                disabled={isGeneratingAi}
                autoFocus
                onChange={(e) => setNewRegionInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmAddRegion();
                  } else if (e.key === 'Escape') {
                    setIsAddingRegion(false);
                    setNewRegionInput('');
                  }
                }}
              />
              <button
                type="button"
                className="cmp-btn-sm-add"
                disabled={isGeneratingAi}
                onClick={handleConfirmAddRegion}
              >
                Add
              </button>
              <button
                type="button"
                className="cmp-btn-sm-cancel"
                disabled={isGeneratingAi}
                onClick={() => {
                  setIsAddingRegion(false);
                  setNewRegionInput('');
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <select
              className="cmp-field-select"
              value={formData.targetRegion}
              disabled={isReadOnly || isGeneratingAi}
              onChange={(e) => {
                if (isReadOnly || isGeneratingAi) return;
                if (e.target.value === '__ADD_NEW__') {
                  setIsAddingRegion(true);
                  return;
                }
                setFormData({ ...formData, targetRegion: e.target.value });
                if (validationError) setValidationError('');
              }}
            >
              <option value="">Select Target Region...</option>
              {customTargetRegions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
              <option value="__ADD_NEW__">+ Add Custom Target Region...</option>
            </select>
          )}
        </div>

        {/* Allocated Budget */}
        <div className="cmp-field-group">
          <label className="cmp-field-label">
            <span>
              Allocated Budget <span className="text-danger">*</span>
            </span>
          </label>
          <div
            className={`cmp-currency-budget-group ${isReadOnly || isGeneratingAi ? 'is-disabled' : ''}`}
          >
            <div className="cmp-currency-picker-wrap" ref={currencyPopoverRef}>
              <button
                type="button"
                className="cmp-currency-trigger-btn"
                disabled={isReadOnly || isGeneratingAi}
                onClick={() => setShowCurrencyPopover((prev) => !prev)}
                title={`Current: ${currentCurrency} (${(CURRENCY_SYMBOLS[currentCurrency] || currentCurrency).trim()}) • Click to select currency`}
              >
                <div className="cmp-currency-trigger-flag">
                  <img
                    src={`https://flagcdn.com/w40/${CURRENCY_COUNTRY_CODES[currentCurrency] || 'un'}.png`}
                    alt={currentCurrency}
                  />
                </div>
                <span className="cmp-currency-trigger-code">{currentCurrency}</span>
                <span className="cmp-currency-trigger-symbol">
                  {CURRENCY_SYMBOLS[currentCurrency] || currentCurrency}
                </span>
                <i
                  className={`fas fa-chevron-${showCurrencyPopover ? 'up' : 'down'} cmp-currency-trigger-chevron`}
                />
              </button>

              {showCurrencyPopover && (
                <div className="crm-currency-popover">
                  {/* Top Header with Live FX Badge */}
                  <div className="crm-currency-popover-header">
                    <div className="crm-currency-header-left">
                      <i className="fas fa-coins crm-currency-header-icon" />
                      <span className="crm-currency-popover-title">Currency</span>
                    </div>
                    <span className="crm-currency-latest-badge">
                      <span className="crm-currency-latest-dot" />
                      <span className="crm-currency-latest-text">Live FX</span>
                      <span className="crm-currency-latest-shine" />
                    </span>
                  </div>

                  {/* Quick Search Filter */}
                  <div className="crm-currency-search-wrap">
                    <i className="fas fa-search crm-currency-search-icon" />
                    <input
                      type="text"
                      className="crm-currency-search-input"
                      placeholder="Search currency or country..."
                      value={currencySearch}
                      autoFocus
                      onChange={(e) => setCurrencySearch(e.target.value)}
                    />
                    {currencySearch && (
                      <button
                        type="button"
                        className="crm-currency-search-clear"
                        onClick={() => setCurrencySearch('')}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>

                  {/* Filtered Currency List */}
                  <div className="crm-currency-popover-list">
                    {CURRENCIES.filter((curr) => {
                      if (!currencySearch.trim()) return true;
                      const q = currencySearch.toLowerCase().trim();
                      const name = (CURRENCY_NAMES[curr] || '').toLowerCase();
                      const sym = (CURRENCY_SYMBOLS[curr] || '').toLowerCase();
                      return curr.toLowerCase().includes(q) || name.includes(q) || sym.includes(q);
                    }).map((curr) => {
                      const isSelected = curr === currentCurrency;
                      const symbol = CURRENCY_SYMBOLS[curr]?.trim() || curr;
                      const name = CURRENCY_NAMES[curr] || curr;
                      const countryCode = CURRENCY_COUNTRY_CODES[curr] || 'un';
                      const rateVal =
                        fxRates && fxRates[curr]
                          ? Number(fxRates[curr]).toFixed(curr === 'JPY' ? 1 : 2)
                          : null;

                      return (
                        <button
                          key={curr}
                          type="button"
                          className={`crm-currency-popover-item${isSelected ? ' selected' : ''}`}
                          onClick={() => {
                            handleCurrencyChange(curr);
                            setShowCurrencyPopover(false);
                            setCurrencySearch('');
                          }}
                        >
                          <div className="crm-currency-flag-container">
                            <img
                              src={`https://flagcdn.com/w40/${countryCode}.png`}
                              srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
                              alt={name}
                              className="crm-currency-flag-img"
                              loading="lazy"
                            />
                          </div>
                          <div className="crm-currency-info">
                            <div className="crm-currency-code-row">
                              <span className="crm-currency-item-code">{curr}</span>
                              <span className="crm-currency-item-symbol">{symbol}</span>
                            </div>
                            <span className="crm-currency-item-name">{name}</span>
                          </div>
                          <div className="crm-currency-right-meta">
                            {rateVal && curr !== 'USD' && (
                              <span
                                className="crm-currency-rate-badge"
                                title={`1 USD = ${rateVal} ${curr}`}
                              >
                                {rateVal}
                              </span>
                            )}
                            {isSelected && (
                              <span className="crm-currency-check-wrap">
                                <i className="fas fa-check crm-currency-item-check" />
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Effective Date & Sync Footer */}
                  <div className="crm-currency-popover-footer">
                    <div className="crm-currency-footer-row">
                      <span className="crm-currency-footer-source">
                        <i className="fas fa-exchange-alt" /> Converted from USD base
                      </span>
                      <span className="crm-currency-footer-date">
                        Effective: <strong>{formatEffectiveDate(fxRateMeta?.date)}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <input
              type="text"
              className="cmp-currency-budget-input"
              value={budgetAmountInput}
              disabled={isReadOnly || isGeneratingAi}
              onChange={handleBudgetAmountChange}
              placeholder="e.g. 25,000"
            />
          </div>

          {currentCurrency !== 'USD' && (
            <div className="cmp-fx-rate-indicator">
              <i className="fas fa-chart-line" />
              <span>
                1 USD ={' '}
                {Number(
                  (fxRates && fxRates[currentCurrency]) || FALLBACK_FX_RATES[currentCurrency] || 1
                ).toFixed(currentCurrency === 'JPY' ? 1 : 2)}{' '}
                {currentCurrency} (Effective: {formatEffectiveDate(fxRateMeta?.date)})
              </span>
            </div>
          )}
        </div>

        {/* Campaign Schedule, Duration & Interactive Calendar */}
        <div className="cmp-field-group full-width">
          <CampaignDateRangePicker
            startDate={formData.startDate}
            endDate={formData.endDate}
            durationDays={formData.durationDays}
            disabled={isReadOnly || isGeneratingAi}
            onChange={({ startDate, endDate, durationDays }) => {
              if (isReadOnly || isGeneratingAi) return;
              setFormData((prev) => ({
                ...prev,
                startDate,
                endDate,
                durationDays,
              }));
            }}
          />
        </div>

        {/* Target Personas */}
        <div className="cmp-field-group full-width">
          <label className="cmp-field-label">
            <span>
              Target Decision-Maker Personas ({formData.targetPersonas.length} Selected){' '}
              <span className="text-danger">*</span>
            </span>
            {!isReadOnly && !isGeneratingAi && !isAddingPersona && (
              <button
                type="button"
                className="cmp-btn-inline-add-custom"
                onClick={() => setIsAddingPersona(true)}
                title="Add a custom persona for this session"
              >
                <i className="fas fa-plus" /> Custom Persona
              </button>
            )}
          </label>

          {isAddingPersona && (
            <div className="cmp-custom-add-row" style={{ marginBottom: '10px' }}>
              <input
                type="text"
                className="cmp-field-input-sm"
                placeholder="Enter custom decision-maker persona (e.g. Chief Innovation Officer)..."
                value={newPersonaInput}
                disabled={isGeneratingAi}
                autoFocus
                onChange={(e) => setNewPersonaInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirmAddPersona();
                  } else if (e.key === 'Escape') {
                    setIsAddingPersona(false);
                    setNewPersonaInput('');
                  }
                }}
              />
              <button
                type="button"
                className="cmp-btn-sm-add"
                disabled={isGeneratingAi}
                onClick={handleConfirmAddPersona}
              >
                Add
              </button>
              <button
                type="button"
                className="cmp-btn-sm-cancel"
                disabled={isGeneratingAi}
                onClick={() => {
                  setIsAddingPersona(false);
                  setNewPersonaInput('');
                }}
              >
                Cancel
              </button>
            </div>
          )}

          <div className="cmp-tag-picker-wrap">
            {customPersonas.map((p) => {
              const isSelected = formData.targetPersonas.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  disabled={isReadOnly || isGeneratingAi}
                  className={`cmp-tag-pill ${isSelected ? 'selected' : ''} ${isReadOnly || isGeneratingAi ? 'read-only' : ''}`}
                  onClick={() => !isReadOnly && !isGeneratingAi && onTogglePersona(p)}
                >
                  {isSelected && <i className="fas fa-check" />} {p}
                </button>
              );
            })}
            {!isReadOnly && !isGeneratingAi && !isAddingPersona && (
              <button
                type="button"
                className="cmp-tag-pill-add-custom"
                onClick={() => setIsAddingPersona(true)}
                title="Add a custom persona"
              >
                <i className="fas fa-plus" /> Custom Persona
              </button>
            )}
          </div>
        </div>

        {/* Channels Selection */}
        <div className="cmp-field-group full-width">
          <label className="cmp-field-label">
            <span>
              Active Outreach Communication Channels <span className="text-danger">*</span>
            </span>
          </label>
          <div className="cmp-channels-toggle-row">
            {ALL_CHANNELS.map((ch) => {
              const isChecked = formData.channels.includes(ch.id);
              return (
                <div
                  key={ch.id}
                  className={`cmp-channel-select-card ${isChecked ? 'active' : ''} ${isReadOnly || isGeneratingAi ? 'read-only' : ''}`}
                  onClick={() => !isReadOnly && !isGeneratingAi && onToggleChannel(ch.id)}
                >
                  <i className={ch.icon} />
                  <span>{ch.label}</span>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isReadOnly || isGeneratingAi}
                    onChange={() => {}}
                    tabIndex={-1}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
